import React, { useState, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { Calendar } from "lucide-react";
import { FormField, FormItem, FormMessage } from "./form";
import { DateTimePickerModal } from "./date-time-picker-modal";
import { formatDateTime, parseDateTime } from "../../utils/dateTime";
import { TimeSlot } from "../../types/asset";
import { UI_CONFIG } from "../../constants/autostack";

interface DateTimeInputProps {
  name: string;
  placeholder?: string;
  className?: string;
  label?: string;
  disablePastDates?: boolean;
}

export const DateTimeInput: React.FC<DateTimeInputProps> = React.memo(
  ({
    name,
    placeholder = "Select date and time",
    className = "",
    label = "Date and time",
    disablePastDates = false,
  }) => {
    const form = useFormContext();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const currentValue = form.watch(name);
    const parsedDateTime = parseDateTime(currentValue);

    const handleDateTimeSelect = useCallback(
      (date: Date, time: TimeSlot) => {
        const formattedDateTime = formatDateTime(date, time);
        form.setValue(name, formattedDateTime);
        setIsModalOpen(false);
      },
      [form, name]
    );

    const handleOpenModal = useCallback(() => {
      setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
      setIsModalOpen(false);
    }, []);

    return (
      <>
        <FormField
          control={form.control}
          name={name}
          render={({ field }) => (
            <FormItem className={className}>
              <div className="flex flex-col gap-1">
                <div className="relative">
                  <button
                    type="button"
                    onClick={handleOpenModal}
                    className={`
                    flex items-center justify-between w-full ${UI_CONFIG.FORM_HEIGHT} px-4 pr-12 
                    border border-gray-200 rounded-md bg-white hover:bg-gray-50 
                    focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 
                    transition-colors text-left
                  `}
                    aria-label={`Select ${label}`}
                  >
                    <span
                      className={
                        currentValue ? "text-gray-900" : "text-gray-500"
                      }
                    >
                      {currentValue || placeholder}
                    </span>
                  </button>
                  <Calendar
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none"
                    aria-hidden="true"
                  />
                </div>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />

        <DateTimePickerModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onDateTimeSelect={handleDateTimeSelect}
          selectedDate={parsedDateTime?.date || new Date()}
          selectedTime={parsedDateTime?.time}
          title={`Select ${label}`}
          disablePastDates={disablePastDates}
        />
      </>
    );
  }
);

DateTimeInput.displayName = "DateTimeInput";
